import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Grid,
  MenuItem,
  Snackbar,
  Alert,
  useTheme,
  alpha,
  CircularProgress
} from '@mui/material';
import { Send, Event } from '@mui/icons-material';
import { timeOptions, methodOptions } from '../config/contactConfig';
import { createProspect } from '../services/prospectService';

const ScheduleCallForm = ({ inModal = false, onSuccess = null }) => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    preferredTime: '',
    preferredMethod: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    if (!formData.name || !formData.email) {
      setErrorMessage('Name and email are required fields');
      setShowError(true);
      return;
    }

    setLoading(true);

    try {
      // Call the createProspect service
      await createProspect(formData);

      // Show success message
      setShowSuccess(true);

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        preferredTime: '',
        preferredMethod: '',
        message: ''
      });

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setErrorMessage(error.response?.data?.message || 'An error occurred while submitting the form. Please try again.');
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  // Form content
  const formContent = (
    <>
      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {/* Personal Information Section */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: theme.palette.primary.main }}>
            Your Information
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                variant="outlined"
                InputProps={{
                  sx: { borderRadius: 3 }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                variant="outlined"
                InputProps={{
                  sx: { borderRadius: 3 }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone Number"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                variant="outlined"
                placeholder="(555) 123-4567"
                InputProps={{
                  sx: { borderRadius: 3 }
                }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Contact Preferences Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: theme.palette.primary.main }}>
            Call Preferences
          </Typography>
          <Box 
            sx={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 4,
              '@media (max-width: 600px)': {
                gridTemplateColumns: '1fr'
              }
            }}>
            <Box>
              <TextField
                select
                fullWidth
                label="Best Time to Call"
                name="preferredTime"
                value={formData.preferredTime}
                onChange={handleChange}
                variant="outlined"
                InputProps={{
                  sx: { borderRadius: 3 }
                }}
              >
                {timeOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Box>
              <TextField
                select
                fullWidth
                label="Preferred Contact Method"
                name="preferredMethod"
                value={formData.preferredMethod}
                onChange={handleChange}
                variant="outlined"
                sx={{ minWidth: '240px' }}
                InputProps={{
                  sx: { borderRadius: 3 }
                }}
              >
                {methodOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Box>
              {/* Empty third column to maintain 3-column layout */}
            </Box>
          </Box>
        </Box>

        {/* Message Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: theme.palette.primary.main }}>
            Your Message
          </Typography>
          <TextField
            fullWidth
            label="Message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            multiline
            rows={4}
            variant="outlined"
            placeholder="Please let us know what you'd like to discuss..."
            InputProps={{
              sx: { borderRadius: 3 }
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={loading}
            endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Send />}
            sx={{
              px: 8,
              py: 2,
              fontSize: '1.2rem',
              fontWeight: 600,
              borderRadius: 3,
              boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.4)}`
              },
              transition: 'all 0.3s ease'
            }}
          >
            {loading ? 'Submitting...' : 'Schedule Call'}
          </Button>
        </Box>
      </Box>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSuccess(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          Thank you for scheduling a call! We will contact you soon.
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowError(false)}
          severity="error"
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );

  // If in modal, return just the form content
  if (inModal) {
    return formContent;
  }

  // Otherwise, wrap in Paper
  return (
    <Paper
      elevation={4}
      sx={{
        p: { xs: 4, md: 6 },
        borderRadius: 4,
        maxWidth: 800,
        mx: 'auto',
        position: 'relative',
        overflow: 'hidden',
        background: 'white',
        boxShadow: `0 10px 40px ${alpha(theme.palette.primary.main, 0.1)}`,
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: `linear-gradient(135deg, ${alpha('#fff', 0.1)} 0%, transparent 50%)`,
          pointerEvents: 'none'
        }
      }}
    >
      {formContent}
    </Paper>
  );
};

export default ScheduleCallForm;
